import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, LayoutGrid, LogOut, User as UserIcon, Settings } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function Header() {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isAuthed = user && typeof user === "object";

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  const navItems = (
    <>
      <Link data-testid="nav-compare" to="/compare" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
        {t("nav_features") === "Features" ? "Compare" : "तुलना"}
      </Link>
      <Link data-testid="nav-calculators" to="/calculators" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
        {t("nav_calculators")}
      </Link>
      <Link data-testid="nav-guides" to="/guides" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
        {t("nav_guides")}
      </Link>
      <Link data-testid="nav-assistant" to="/assistant" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
        {t("nav_assistant")}
      </Link>
      <Link data-testid="nav-pricing" to="/membership" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
        {t("nav_pricing")}
      </Link>
      {isAuthed && (
        <Link data-testid="nav-dashboard" to="/dashboard" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
          {t("nav_dashboard")}
        </Link>
      )}
      {isAuthed && (
        <Link data-testid="nav-reminders" to="/reminders" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
          {t("nav_reminders")}
        </Link>
      )}
    </>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />

        <nav className="hidden items-center gap-8 md:flex">{navItems}</nav>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher />
          {isAuthed ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-btn" variant="outline" className="min-h-[40px] rounded-full gap-2">
                  <UserIcon className="h-4 w-4" />
                  <span className="max-w-[120px] truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutGrid className="mr-2 h-4 w-4" /> {t("nav_dashboard")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <Settings className="mr-2 h-4 w-4" /> {t("settings_link")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-rose-600 focus:text-rose-600">
                  <LogOut className="mr-2 h-4 w-4" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button data-testid="header-login-btn" variant="ghost" className="min-h-[40px]" onClick={() => navigate("/login")}>
                {t("login")}
              </Button>
              <Button data-testid="header-signup-btn" className="min-h-[40px] rounded-full px-5" onClick={() => navigate("/signup")}>
                {t("signup")}
              </Button>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-btn" variant="outline" size="icon" className="h-10 w-10 rounded-full" aria-label={t("menu")}>
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] max-w-sm">
              <SheetHeader>
                <SheetTitle><Logo /></SheetTitle>
              </SheetHeader>
              <div className="mt-8 flex flex-col gap-5">
                {navItems}
                <div className="h-px bg-slate-200" />
                {isAuthed ? (
                  <Button data-testid="mobile-logout-btn" variant="outline" className="min-h-[48px] justify-start gap-2" onClick={handleLogout}>
                    <LogOut className="h-4 w-4" /> {t("logout")}
                  </Button>
                ) : (
                  <>
                    <Button data-testid="mobile-login-btn" variant="outline" className="min-h-[48px]" onClick={() => { setOpen(false); navigate("/login"); }}>
                      {t("login")}
                    </Button>
                    <Button data-testid="mobile-signup-btn" className="min-h-[48px] rounded-full" onClick={() => { setOpen(false); navigate("/signup"); }}>
                      {t("signup")}
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
